// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  listServiceTypes,
  createServiceType,
  updateServiceType,
  deleteServiceType,
  listBusinessHours,
  createBusinessHours,
  updateBusinessHours,
  listBlackouts,
  createBlackout,
  deleteBlackout,
} from '../../../api/schedulingApi';
import api from '../../../apiClient';
import { getActiveTenant } from '../../../helpers/tenantHelpers';
import { getTeamVocab } from '../../../constants/uiRegistry';
import { deriveWindows, windowLabel, normalizeWindowMinutes } from '../../../components/Schedule/arrivalWindows';
import { FormRow, SaveBar, Section, SectionRail } from './primitives';
import ServiceCatalog from '../../../components/Settings/ServiceCatalog';

/* Industry-specific example copy for placeholders/help, so an events tenant is
   never prompted with "pest service" or "termite cert". Reads the active tenant's
   industry; falls back to neutral examples. Keeps the pest copy for pest tenants. */
const SCHED_EXAMPLES = {
  events:       { service: 'e.g. Saturday reception package', guidance: 'e.g. Ask guest count and event date, confirm the space is free, note setup/teardown time. Deposit holds the date.', skills: 'e.g. bar service, AV' },
  landscaping:  { service: 'e.g. Biweekly lawn maintenance',  guidance: 'e.g. Ask property size and gate code. Crew needs a trailer. Skip in heavy rain.',                             skills: 'e.g. irrigation, hardscape' },
  pest_control: { service: 'e.g. Quarterly pest service',     guidance: 'e.g. Ask square footage and pest type. Only techs with the termite cert. Not offered in winter.',            skills: 'e.g. termite, recheck' },
  general:      { service: 'e.g. Standard service',           guidance: 'e.g. What to ask the customer and any constraints before booking.',                                          skills: 'e.g. certified, senior' },
};
const schedExamples = () => SCHED_EXAMPLES[(getActiveTenant()?.industry || '').toLowerCase()] || SCHED_EXAMPLES.general;

/* Scheduling — service types, business hours, time off.
   Technicians and their availability live under Team.
   Hours are ONE card with 7 rows; edits collect into the page save bar. */

const SECTIONS = [
  { id: 'services', label: 'Service types' },
  { id: 'catalog', label: 'Services & Pricing' },
  { id: 'hours', label: 'Business hours' },
  { id: 'arrival', label: 'Arrival notifications' },
  { id: 'blackouts', label: 'Time off' },
];

/* ARRIVAL NOTIFICATIONS — the one tenant-facing control for how a customer
   learns a tech is on the way.

   Writes preferences.scheduling.arrival_windows.call_ahead_method, declared in
   accounts/preference_schema.py and read by
   scheduling_app/services/en_route.py::call_ahead_method. That declaration is
   what keeps this from being a setting that saves, reloads, and changes
   nothing.

   NOT HERE ON PURPOSE: the ops-level kill switch (comms.en_route_texts). The
   API strips it from GET and refuses it on PATCH, so this screen cannot render
   or send it even by mistake. "Text the customer" therefore describes what the
   office is asking for, not proof that texting is enabled platform-side. */
const ARRIVAL_METHODS = [
  {
    value: 'note',
    label: 'Just note it for the tech',
    blurb: "The tech sees the arrival window on their stop. Nobody contacts the customer ahead, and we don't promise them a call or a text.",
  },
  {
    value: 'tech_calls',
    label: 'The tech calls ahead',
    blurb: 'The tech phones before arriving. We show them the number and the notice this customer expects.',
  },
  {
    value: 'auto_text',
    label: 'Text the customer automatically',
    blurb: "When a tech taps On my way, we send a short text in their name with their ETA. Customers who asked to be phoned still get a call, and anyone opted out of texts is never texted.",
  },
];

const ARRIVAL_MINUTES = [30, 60];

// Arrival-window width options. The promise we make a customer is a WINDOW the
// office can actually hit, not a minute-level time. Mirrors the backend bound
// (scheduling_app/arrival_windows.py ArrivalWindowPolicy: 30-720, default 180).
const ARRIVAL_WINDOW_OPTIONS = [
  { v: 60, label: '1 hour' },
  { v: 120, label: '2 hours' },
  { v: 180, label: '3 hours' },
  { v: 240, label: '4 hours' },
];
// Grid derivation lives in a shared, tested util (a faithful port of the backend
// arrival_windows.derive_grid) so the preview can never drift from what voice/SMS speak.

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const minuteToTime = (mins = 0) => {
  const safe = Number.isFinite(mins) ? mins : 0;
  return `${String(Math.floor(safe / 60)).padStart(2, '0')}:${String(safe % 60).padStart(2, '0')}`;
};
const timeToMinute = (value = '00:00') => {
  const [h, m] = String(value).split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

const seedHours = () => {
  const seed = {};
  WEEKDAYS.forEach((_, idx) => {
    seed[idx] = { id: null, weekday: idx, open_time: '08:00', close_time: '18:00', is_closed: false };
  });
  return seed;
};

// Serializer field names are the live ServiceType (scheduling_app) contract:
// default_duration_minutes / price_display / price_min / price_max /
// description / ai_guidance / is_active / is_bookable. (The old editor wrote
// duration_minutes/base_price, which the current serializer silently drops.)
const emptyService = () => ({
  id: null, name: '', description: '', ai_guidance: '',
  default_duration_minutes: 60, price_display: '', price_min: '', price_max: '',
  is_active: true, is_bookable: true,
  booking_rules: {}, skills_required: [],
});

const fromRow = (s) => ({
  id: s.id,
  name: s.name || '',
  description: s.description || '',
  ai_guidance: s.ai_guidance || '',
  default_duration_minutes: s.default_duration_minutes || 60,
  price_display: s.price_display || '',
  price_min: s.price_min ?? '',
  price_max: s.price_max ?? '',
  is_active: s.is_active !== false,
  is_bookable: s.is_bookable !== false,
  booking_rules: s.booking_rules && typeof s.booking_rules === 'object' ? { ...s.booking_rules } : {},
  skills_required: Array.isArray(s.skills_required) ? s.skills_required : [],
});

const numOrUndef = (v) => (v === '' || v == null ? undefined : Number(v));

// PATCH payload — the fields this editor owns, with numbers/nulls coerced.
// tenant/id/external_id/code are OFF-LIMITS per the agent-surface contract
// (server-side / FieldRoutes linkage). booking_rules keys are the canonical
// shape the agent reads; "qualified techs" is the top-level skills_required.
const toPayload = (form) => {
  const br = {
    ...(form.booking_rules || {}),
    lead_time_hours: numOrUndef(form.booking_rules?.lead_time_hours),
    same_day_ok: !!form.booking_rules?.same_day_ok,
    max_days_out: numOrUndef(form.booking_rules?.max_days_out),
  };
  // drop undefined keys so we don't overwrite with nulls
  Object.keys(br).forEach((k) => br[k] === undefined && delete br[k]);
  return {
    name: form.name,
    description: form.description,
    ai_guidance: form.ai_guidance,
    default_duration_minutes: Number(form.default_duration_minutes) || null,
    price_display: form.price_display,
    price_min: form.price_min === '' || form.price_min == null ? null : Number(form.price_min),
    price_max: form.price_max === '' || form.price_max == null ? null : Number(form.price_max),
    is_active: !!form.is_active,
    is_bookable: !!form.is_bookable,
    booking_rules: br,
    skills_required: Array.isArray(form.skills_required) ? form.skills_required : [],
  };
};

function ServiceEditor({ id, services, onSave, onRemove, onBack }) {
  const isNew = id === 'new';
  const source = React.useMemo(() => {
    if (isNew) return emptyService();
    const s = services.find((row) => String(row.id) === String(id));
    return s ? fromRow(s) : null;
  }, [id, isNew, services]);

  const [form, setForm] = useState(source);
  const [baseline, setBaseline] = useState(source);
  useEffect(() => { setForm(source); setBaseline(source); }, [source]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setRule = (k, v) => setForm((p) => ({ ...p, booking_rules: { ...(p.booking_rules || {}), [k]: v } }));

  const dirty = form && JSON.stringify(form) !== JSON.stringify(baseline);
  const br = (form && form.booking_rules) || {};

  if (!form) {
    return (
      <div className="sv2-col">
        <button className="sv2-back" onClick={onBack}>← All services</button>
        <p className="sv2-section-sub">That service isn't here anymore.</p>
      </div>
    );
  }

  return (
    <div className="sv2-col">
      <button className="sv2-back" onClick={onBack}>← All services</button>

      <Section id="service" title={isNew ? 'New service' : form.name || 'Service'}
        sub="What customers can book, and what the assistant should know about it.">
        <FormRow label="Name" help="What customers see when they book.">
          <input type="text" value={form.name} placeholder={schedExamples().service}
            onChange={(e) => set('name', e.target.value)} />
        </FormRow>
        <FormRow label="Description" help="Customer-facing — shown when booking and read by the assistant.">
          <textarea rows={3} value={form.description}
            placeholder="What's included, who it's for…"
            onChange={(e) => set('description', e.target.value)} />
        </FormRow>
        <FormRow
          label="AI guidance — what the assistant should know / gather"
          help="Internal only. The assistant uses this to answer questions and collect the right details before booking. Customers never see it.">
          <textarea rows={3} value={form.ai_guidance}
            placeholder={schedExamples().guidance}
            onChange={(e) => set('ai_guidance', e.target.value)} />
        </FormRow>
      </Section>

      <Section id="pricing" title="Pricing & length"
        sub="Shown to customers; the range guides the assistant's quotes.">
        <FormRow label="Price display" help='Customer-facing label, e.g. "Starting at $99".'>
          <input type="text" value={form.price_display} placeholder="Starting at $99"
            onChange={(e) => set('price_display', e.target.value)} />
        </FormRow>
        <FormRow label="Price range" help="Optional min / max in dollars — bounds the assistant's estimate.">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <input type="number" min="0" step="1" value={form.price_min ?? ''} placeholder="Min $"
              onChange={(e) => set('price_min', e.target.value)} />
            <input type="number" min="0" step="1" value={form.price_max ?? ''} placeholder="Max $"
              onChange={(e) => set('price_max', e.target.value)} />
          </div>
        </FormRow>
        <FormRow label="Usual length" help="In minutes — sets the default appointment slot.">
          <input type="number" min="5" step="5" value={form.default_duration_minutes || ''}
            onChange={(e) => set('default_duration_minutes', Number(e.target.value) || 0)} />
        </FormRow>
      </Section>

      <Section id="booking" title="Booking rules"
        sub="How far ahead this can be booked, and which techs are qualified. The assistant honors these.">
        <FormRow label="Minimum lead time (hours)" help="How far in advance a customer must book. Leave blank for no minimum.">
          <input type="number" min="0" step="1" value={br.lead_time_hours ?? ''} placeholder="e.g. 24"
            onChange={(e) => setRule('lead_time_hours', e.target.value === '' ? undefined : Number(e.target.value))} />
        </FormRow>
        <FormRow label="Allow same-day booking" help="Let customers book this for today.">
          <label className="sv2-closed">
            <input type="checkbox" checked={!!br.same_day_ok}
              onChange={(e) => setRule('same_day_ok', e.target.checked)} /> Same-day allowed
          </label>
        </FormRow>
        <FormRow label="Book at most … days out" help="How far into the future this can be scheduled. Leave blank for no limit.">
          <input type="number" min="1" step="1" value={br.max_days_out ?? ''} placeholder="e.g. 60"
            onChange={(e) => setRule('max_days_out', e.target.value === '' ? undefined : Number(e.target.value))} />
        </FormRow>
        <FormRow label="Required skills"
          help={`Comma-separated. Only ${getTeamVocab().members} with these skills can be booked for this service.`}>
          <input type="text" value={(form.skills_required || []).join(', ')} placeholder={schedExamples().skills}
            onChange={(e) => set('skills_required', e.target.value.split(',').map((x) => x.trim()).filter(Boolean))} />
        </FormRow>
      </Section>

      <Section id="visibility" title="Visibility"
        sub="Whether this service is active and open for booking.">
        <FormRow label="Active" help="Turn off to retire a service without deleting its history.">
          <label className="sv2-closed">
            <input type="checkbox" checked={form.is_active}
              onChange={(e) => set('is_active', e.target.checked)} /> Active
          </label>
        </FormRow>
        <FormRow label="Bookable" help="Off = visible internally but customers and the assistant can't book it.">
          <label className="sv2-closed">
            <input type="checkbox" checked={form.is_bookable}
              onChange={(e) => set('is_bookable', e.target.checked)} /> Open for booking
          </label>
        </FormRow>
        {!isNew && (
          <div className="sv2-actions">
            <button className="sv2-btn danger" onClick={() => onRemove(form)}>Remove this service</button>
          </div>
        )}
      </Section>

      <SaveBar dirty={!!dirty && !!form.name?.trim()} saving={false}
        onSave={() => onSave(form)}
        onDiscard={() => (isNew ? onBack() : setForm(baseline))} />
    </div>
  );
}

export default function SchedulingTab({ sub, id }) {
  const navigate = useNavigate();
  const tenantId = getActiveTenant()?.id || null;
  const [services, setServices] = useState([]);
  const [hours, setHours] = useState(seedHours());
  const [hoursBaseline, setHoursBaseline] = useState(seedHours());
  const [blackouts, setBlackouts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [blackoutDraft, setBlackoutDraft] = useState({ start_at: '', end_at: '', reason: '' });

  // Arrival notifications. Default 'note' because that is what the backend
  // resolves an unset value to — the screen must not imply a different default
  // than the one actually in force.
  const [arrivalMethod, setArrivalMethod] = useState('note');
  const [arrivalMinutes, setArrivalMinutes] = useState(30);
  const [arrivalWindowMins, setArrivalWindowMins] = useState(180);
  const [arrivalLoading, setArrivalLoading] = useState(true);
  const [arrivalSaving, setArrivalSaving] = useState(false);
  const [arrivalError, setArrivalError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get('/accounts/preferences/');
        const aw = data?.preferences?.scheduling?.arrival_windows || {};
        if (!mounted) return;
        const stored = String(aw.call_ahead_method || '');
        setArrivalMethod(ARRIVAL_METHODS.some((m) => m.value === stored) ? stored : 'note');
        setArrivalMinutes(
          ARRIVAL_MINUTES.includes(Number(aw.call_ahead_minutes)) ? Number(aw.call_ahead_minutes) : 30,
        );
        setArrivalWindowMins(normalizeWindowMinutes(aw.window_minutes));
        setArrivalError('');
      } catch {
        if (mounted) setArrivalError("Couldn't load this office's arrival settings.");
      } finally {
        if (mounted) setArrivalLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const saveArrival = async () => {
    setArrivalSaving(true);
    try {
      // Send ONLY this namespace. The preferences PATCH is a deep merge, so a
      // narrow payload cannot clobber another screen's settings — round-tripping
      // the whole blob is how settings screens overwrite each other.
      await api.patch('/accounts/preferences/', {
        preferences: {
          scheduling: {
            arrival_windows: {
              call_ahead_method: arrivalMethod,
              call_ahead_minutes: arrivalMinutes,
              window_minutes: arrivalWindowMins,
            },
          },
        },
      });
      // Read back rather than trusting the 200: the API drops paths it will not
      // accept, so a "Saved" toast over a discarded write is exactly the
      // failure this re-read catches.
      const { data } = await api.get('/accounts/preferences/');
      const savedAw = data?.preferences?.scheduling?.arrival_windows || {};
      const saved = String(savedAw.call_ahead_method || '');
      const savedWin = Number(savedAw.window_minutes);
      if (saved === arrivalMethod && savedWin === arrivalWindowMins) {
        toast.success('Arrival notifications saved');
        setArrivalError('');
      } else {
        setArrivalError("The server didn't keep that change. Nothing was saved.");
        toast.error("That didn't save");
      }
    } catch {
      setArrivalError("Couldn't save. Nothing was changed.");
      toast.error("Couldn't save arrival notifications");
    } finally {
      setArrivalSaving(false);
    }
  };
  const editingService = sub === 'services' && id;

  const dirty = useMemo(
    () => JSON.stringify(hours) !== JSON.stringify(hoursBaseline),
    [hours, hoursBaseline]
  );

  const load = async () => {
    try {
      const [svc, hrs, blk] = await Promise.all([
        listServiceTypes(), listBusinessHours(), listBlackouts(),
      ]);
      const svcRows = Array.isArray(svc.data) ? svc.data : svc.data?.results || [];
      const hrsRows = Array.isArray(hrs.data) ? hrs.data : hrs.data?.results || [];
      const blkRows = Array.isArray(blk.data) ? blk.data : blk.data?.results || [];
      setServices(svcRows);
      const seed = seedHours();
      hrsRows.forEach((row) => {
        seed[row.weekday] = {
          id: row.id, weekday: row.weekday,
          open_time: minuteToTime(row.open_minute),
          close_time: minuteToTime(row.close_minute),
          is_closed: !!row.is_closed,
        };
      });
      setHours(seed);
      setHoursBaseline(JSON.parse(JSON.stringify(seed)));
      setBlackouts(blkRows);
    } catch {
      toast.error('Could not load scheduling settings');
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const setDay = (weekday, field, value) =>
    setHours((prev) => ({ ...prev, [weekday]: { ...prev[weekday], [field]: value } }));

  const saveHours = async () => {
    setSaving(true);
    try {
      for (const idx of Object.keys(hours)) {
        const row = hours[idx];
        const base = hoursBaseline[idx];
        if (JSON.stringify(row) === JSON.stringify(base)) continue;
        const payload = {
          tenant: tenantId, weekday: row.weekday,
          open_minute: timeToMinute(row.open_time),
          close_minute: timeToMinute(row.close_time),
          is_closed: !!row.is_closed,
        };
        if (row.id) await updateBusinessHours(row.id, payload);
        else {
          const { data } = await createBusinessHours(payload);
          row.id = data.id;
        }
      }
      setHoursBaseline(JSON.parse(JSON.stringify(hours)));
      toast.success('Business hours saved');
    } catch {
      toast.error('Could not save business hours');
    } finally {
      setSaving(false);
    }
  };

  const saveService = async (svc) => {
    try {
      const payload = toPayload(svc);
      // CREATE requires tenant: the ServiceTypeSerializer is fields="__all__" with
      // tenant NOT read-only, so is_valid() rejects a create without it (400
      // "tenant: This field is required") before the mixin's perform_create can
      // inject it. Send it on create; the mixin re-applies the server-side tenant
      // anyway, so a spoofed value can't stick. PATCH is partial → tenant not needed.
      if (svc.id) await updateServiceType(svc.id, payload);
      else await createServiceType({ tenant: tenantId, ...payload });
      toast.success('Service saved');
      load();
      navigate('/settings/scheduling');
    } catch {
      toast.error('Could not save the service');
    }
  };

  const removeService = async (svc) => {
    if (!window.confirm(`Remove "${svc.name}" from your bookable services?`)) return;
    try {
      await deleteServiceType(svc.id);
      setServices((prev) => prev.filter((s) => s.id !== svc.id));
      toast.success('Service removed');
      navigate('/settings/scheduling');
    } catch {
      toast.error('Could not remove the service');
    }
  };

  const addBlackout = async () => {
    if (!blackoutDraft.start_at || !blackoutDraft.end_at) {
      toast.error('Pick a start and end');
      return;
    }
    try {
      const { data } = await createBlackout({ ...blackoutDraft, tenant: tenantId });
      setBlackouts((prev) => [...prev, data]);
      setBlackoutDraft({ start_at: '', end_at: '', reason: '' });
      toast.success('Time off added');
    } catch {
      toast.error('Could not add time off');
    }
  };

  const removeBlackout = async (row) => {
    try {
      await deleteBlackout(row.id);
      setBlackouts((prev) => prev.filter((b) => b.id !== row.id));
    } catch {
      toast.error('Could not remove it');
    }
  };

  const fmtWhen = (value) => {
    try { return new Date(value).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
    catch { return value; }
  };

  /* Service editor route: /settings/scheduling/services/:id — same shell,
     same column, back to the list. */
  if (editingService) {
    return (
      <div className="sv2-body">
        <ServiceEditor id={id} services={services}
          onSave={saveService} onRemove={removeService}
          onBack={() => navigate('/settings/scheduling')} />
      </div>
    );
  }

  return (
    <div className="sv2-body">
      <SectionRail sections={SECTIONS} />
      <div className="sv2-col">
        {/* ── Service types ── */}
        <Section id="services" title="Service types"
          sub="What customers can book — each with its usual length and price.">
          <div className="sv2-obj-list">
            {services.length === 0 && (
              <p className="sv2-section-sub">No services yet — add your first one below.</p>
            )}
            {services.map((s) => (
              <div className="sv2-obj" key={s.id}>
                <div className="sv2-obj-head">
                  <div style={{ minWidth: 0 }}>
                    <div className="sv2-obj-title">
                      {s.name}
                      {s.is_active === false ? <span className="sv2-pill off"> Inactive</span> : null}
                      {s.is_bookable === false ? <span className="sv2-pill off"> Not bookable</span> : null}
                    </div>
                    <div className="sv2-obj-sub">
                      {[s.default_duration_minutes ? `${s.default_duration_minutes} min` : null,
                        s.price_display || null]
                        .filter(Boolean).join(' · ') || 'No details yet'}
                    </div>
                  </div>
                  <button className="sv2-btn sm" onClick={() => navigate(`/settings/scheduling/services/${s.id}`)}>Edit</button>
                </div>
              </div>
            ))}
          </div>
          <div className="sv2-actions">
            <button className="sv2-btn primary"
              onClick={() => navigate('/settings/scheduling/services/new')}>
              + Add a service
            </button>
          </div>
        </Section>

        {/* ── Services & Pricing (offerings) — right next to Service types, with
            a plain-language explainer of how the two differ. ── */}
        <Section id="catalog" title="Services & Pricing"
          sub="What you sell and what it costs. A service type (above) is how a service gets scheduled — its length and booking rules; a service here is the thing a customer buys, and whether it's recurring.">
          <ServiceCatalog />
        </Section>

        {/* ── Business hours: ONE card, 7 rows ── */}
        <Section id="hours" title="Business hours"
          sub="When you take appointments. Changes save together from the bar below.">
          <div className="sv2-obj">
            <div className="sv2-hours">
              <div className="sv2-hours-row head">
                <span>Day</span><span>Open</span><span>Close</span><span>Closed</span>
              </div>
              {WEEKDAYS.map((label, idx) => {
                const row = hours[idx];
                return (
                  <div className="sv2-hours-row" key={label}>
                    <span>{label}</span>
                    <input type="time" value={row.open_time} disabled={row.is_closed}
                      onChange={(e) => setDay(idx, 'open_time', e.target.value)} />
                    <input type="time" value={row.close_time} disabled={row.is_closed}
                      onChange={(e) => setDay(idx, 'close_time', e.target.value)} />
                    <label className="sv2-closed">
                      <input type="checkbox" checked={row.is_closed}
                        onChange={(e) => setDay(idx, 'is_closed', e.target.checked)} />
                      Closed
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        </Section>

        {/* ── Time off ── */}
        <Section id="blackouts" title="Time off"
          sub="Holidays and closures — nothing gets booked during these.">
          <div className="sv2-obj-list">
            {blackouts.map((b) => (
              <div className="sv2-obj" key={b.id}>
                <div className="sv2-obj-head">
                  <div style={{ minWidth: 0 }}>
                    <div className="sv2-obj-title">{b.reason || 'Closed'}</div>
                    <div className="sv2-obj-sub">{fmtWhen(b.start_at)} → {fmtWhen(b.end_at)}</div>
                  </div>
                  <button className="sv2-btn sm danger" onClick={() => removeBlackout(b)}>Remove</button>
                </div>
              </div>
            ))}
          </div>
          <FormRow label="Add time off" help="Start and end, plus an optional reason.">
            <div style={{ display: 'grid', gap: 6 }}>
              <input type="datetime-local" value={blackoutDraft.start_at}
                onChange={(e) => setBlackoutDraft((p) => ({ ...p, start_at: e.target.value }))} />
              <input type="datetime-local" value={blackoutDraft.end_at}
                onChange={(e) => setBlackoutDraft((p) => ({ ...p, end_at: e.target.value }))} />
              <input type="text" placeholder="Reason (optional)" value={blackoutDraft.reason}
                onChange={(e) => setBlackoutDraft((p) => ({ ...p, reason: e.target.value }))} />
              <button className="sv2-btn" onClick={addBlackout}>Add</button>
            </div>
          </FormRow>
          <p className="sv2-section-sub">
            Looking for technicians and their availability? They live under the Team tab now.
          </p>
        </Section>

        {/* ── Arrival notifications ── */}
        <Section id="arrival" title="Arrival notifications"
          sub={`How a customer finds out someone is on the way. Applies to every ${getTeamVocab().member} in this office.`}>
          {arrivalLoading ? (
            <p className="sv2-section-sub">Loading…</p>
          ) : arrivalError ? (
            <p className="sv2-section-sub">{arrivalError}</p>
          ) : (
            <>
              {ARRIVAL_METHODS.map((m) => (
                <label key={m.value} className="sv2-closed" style={{ alignItems: 'flex-start' }}>
                  <input
                    type="radio"
                    name="call_ahead_method"
                    checked={arrivalMethod === m.value}
                    onChange={() => setArrivalMethod(m.value)}
                  />
                  <span>
                    <strong>{m.label}</strong>
                    <span className="sv2-section-sub" style={{ display: 'block', marginTop: 2 }}>
                      {m.blurb}
                    </span>
                  </span>
                </label>
              ))}

              {arrivalMethod !== 'note' && (
                <FormRow
                  label="How much notice"
                  help={arrivalMethod === 'auto_text'
                    ? 'Roughly how far ahead the text goes out. A customer whose own record asks for a specific amount of notice keeps that, not this.'
                    : 'How far ahead the tech should call. A customer whose own record asks for a specific amount of notice keeps that, not this.'}
                >
                  <div style={{ display: 'flex', gap: 6 }}>
                    {ARRIVAL_MINUTES.map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={`sv2-btn${arrivalMinutes === n ? ' primary' : ''}`}
                        onClick={() => setArrivalMinutes(n)}
                      >
                        {n} minutes
                      </button>
                    ))}
                  </div>
                </FormRow>
              )}

              <FormRow
                label="Arrival window width"
                help="The window you promise a customer — a span the office can actually hit, never a minute-level time. The grid below is built from your business hours above; a short tail folds into the window before it, and no window ends after you close."
              >
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {ARRIVAL_WINDOW_OPTIONS.map((w) => (
                    <button
                      key={w.v}
                      type="button"
                      className={`sv2-btn${arrivalWindowMins === w.v ? ' primary' : ''}`}
                      onClick={() => setArrivalWindowMins(w.v)}
                    >
                      {w.label}
                    </button>
                  ))}
                </div>
              </FormRow>

              {/* Derived grid preview — mirrors what voice and SMS speak. Reads the
                  business-hours rows above, so it updates as you change hours or width. */}
              <div className="sv2-obj" style={{ marginTop: 4 }}>
                <div className="sv2-obj-sub" style={{ marginBottom: 6 }}>
                  Arrival windows customers will be offered
                </div>
                {WEEKDAYS.map((label, idx) => {
                  const row = hours[idx] || {};
                  if (row.is_closed) return null;
                  const wins = deriveWindows(
                    timeToMinute(row.open_time), timeToMinute(row.close_time), arrivalWindowMins,
                  );
                  if (!wins.length) return null;
                  return (
                    <div key={idx} style={{ display: 'flex', gap: 10, fontSize: 12, color: 'var(--text-2)', padding: '2px 0' }}>
                      <span style={{ width: 84, flex: '0 0 auto', fontWeight: 600 }}>{label}</span>
                      <span>{wins.map(windowLabel).join('  ·  ')}</span>
                    </div>
                  );
                })}
                {WEEKDAYS.every((_, idx) => (hours[idx] || {}).is_closed) && (
                  <div className="sv2-section-sub">Set your business hours above to see the windows.</div>
                )}
              </div>

              <button className="sv2-btn primary" onClick={saveArrival} disabled={arrivalSaving}>
                {arrivalSaving ? 'Saving…' : 'Save arrival notifications'}
              </button>
            </>
          )}
        </Section>
      </div>

      <SaveBar dirty={dirty} saving={saving} onSave={saveHours}
        onDiscard={() => setHours(JSON.parse(JSON.stringify(hoursBaseline)))} />
    </div>
  );
}
